// The sidebar navigation icon set.
//
// One icon per nav entry, because the collapsing rail shows nothing but the icon:
// a missing one would leave a blank row that no amount of muscle memory can read.
//
// Drawing rules — every glyph obeys them, so the set reads as one hand:
//   * a 24×24 viewBox, artwork inset ~2.5 units from the edge
//   * stroke only, never fill: `fill="none"` + `stroke="currentColor"` live on the
//     <svg> (styled in shell.css via .app-nav__icon), so an icon takes the colour
//     of its row in every state — muted, hover, active — with no per-icon rules
//   * 1.6 stroke width, round caps and joins
//   * aria-hidden + focusable="false": the label is the accessible name, and the
//     icon must not become a second one (or an IE/Edge tab stop).
//
// The learning/administration/reporting glyphs are the ones the Verso mockup drew
// (~/dev/clms-design/index.html), transcribed unchanged. The certificates glyph and
// the two platform ones were drawn here to the same rules — the mockup's nav never
// showed those entries. (The rosette was out between 12.9 and 14.9.2026, while
// "Omat todistukset" was reachable only as a tab; nav-icons.test.mjs fails on a
// glyph no entry renders, so it left and came back with the entry.)
//
// The mockup's `content` glyph — a document with a folded corner, drawn for
// "Sisällönhallinta" — left with its entry on 3.11.2026, when that row became the two
// rows `courses` and `questionnaires` (task c4a71fc1). It is not kept "in case":
// nav-icons.test.mjs fails on a glyph no entry renders, and the rosette's trip out and
// back is the precedent for drawing one again if a row ever needs it.

import type { ReactNode } from "react";

export type NavIconName =
  | "myTrainings"
  | "certificates"
  | "certificateOps"
  | "myQuestionnaires"
  | "courses"
  | "questionnaires"
  | "assign"
  | "groups"
  | "users"
  | "organization"
  | "viewScopes"
  | "reports"
  | "audit"
  | "leadershipReports"
  | "questionnaireResults"
  | "changePrograms"
  | "changeMap"
  | "changeActions"
  | "employmentRecords";

const PATHS: Record<NavIconName, ReactNode> = {
  // House — "where I live in this app".
  myTrainings: <path d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />,

  // Rosette with a ribbon — the seal on an awarded certificate.
  certificates: (
    <>
      <circle cx="12" cy="9" r="5.5" />
      <path d="M8.3 13.5 7 21l5-2.5 5 2.5-1.3-7.5" />
    </>
  ),

  // The same rosette, smaller and shifted up-left, with a circular arrow beside it — a certificate
  // that something is being DONE to, which is what the operations page is for. It shares the
  // rosette on purpose (the row is about certificates) and is told apart from the learner entry
  // two sections above by the arrow, the one closed loop in the whole set: at 20px the silhouette
  // is "seal + circle", which no other glyph here is.
  certificateOps: (
    <>
      <circle cx="9.5" cy="8.5" r="5" />
      <path d="M6.2 12.6 5 20l4.5-2.3 2.6 1.3" />
      <path d="M15.2 15.8a3.6 3.6 0 1 1 3.6 3.6" />
      <path d="M16.9 14.1l-1.7 1.7 1.7 1.7" />
    </>
  ),

  // Clipboard with two option rows — a questionnaire is the choices it asks for, and the
  // clip separates it from the two documents in this set (content's folded corner, audit's
  // tick), which a 20px rail reduces to the same rounded rectangle.
  myQuestionnaires: (
    <>
      <path d="M9.5 3.5h5a1 1 0 0 1 1 1V6h-7V4.5a1 1 0 0 1 1-1z" />
      <path d="M8.5 5H6.5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2" />
      <circle cx="9.5" cy="11" r="1.1" />
      <circle cx="9.5" cy="15.5" r="1.1" />
      <path d="M12 11h4.5M12 15.5h4.5" />
    </>
  ),

  // An open book — two pages spread from a spine. The KURSSIT row (3.11.2026), and drawn as a book
  // rather than as the document the mockup's "Sisällönhallinta" carried, because that document was
  // the third portrait sheet in this set: `audit` is a page with a tick and `myQuestionnaires` a
  // clipboard, and at 20px all three reduced to the same rounded rectangle. The book is the one
  // silhouette here that is WIDER than it is tall and symmetrical about a centre line, which no other
  // glyph in the set is.
  courses: (
    <>
      <path d="M12 7.8C10.1 6.1 7.4 5.3 4.2 5.5V18c3.2-.2 5.9.6 7.8 2.3" />
      <path d="M12 7.8c1.9-1.7 4.6-2.5 7.8-2.3V18c-3.2-.2-5.9.6-7.8 2.3" />
      <path d="M12 7.8v12.5" />
    </>
  ),

  // A question mark inside a rounded frame — the KYSELYT management row (3.11.2026). The frame is a
  // SQUARE and the mark is inside it, which is what tells it apart from `myQuestionnaires` two rows
  // above: that one is a portrait clipboard whose silhouette is its clip and its option rows, and this
  // one is a centred mark in a square. The mark is strokes like everything else here, so it takes the
  // row's colour in every state; it is not a letterform the rail would have to render as text.
  questionnaires: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3.5" />
      <path d="M9.6 9.3a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .9-1 1.6v.5" />
      <circle cx="12" cy="16.8" r="0.9" />
    </>
  ),

  // A person with a plus — assigning training to somebody.
  assign: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20v-1.5A3.5 3.5 0 0 1 6.5 15h5a3.5 3.5 0 0 1 3.5 3.5V20" />
      <path d="M19 8.5v5M21.5 11h-5" />
    </>
  ),

  // Two people, one behind the other.
  groups: (
    <>
      <circle cx="8.5" cy="8.5" r="3" />
      <circle cx="16.5" cy="10" r="2.4" />
      <path d="M2.5 19.5v-1a3.5 3.5 0 0 1 3.5-3.5h5a3.5 3.5 0 0 1 3.5 3.5v1" />
      <path d="M16 14.5h1.2a3.3 3.3 0 0 1 3.3 3.3v1.7" />
    </>
  ),

  // A single person.
  users: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5 20v-1.2A4.8 4.8 0 0 1 9.8 14h4.4a4.8 4.8 0 0 1 4.8 4.8V20" />
    </>
  ),

  // An ID card — a landscape card with a small head on the left and two lines of text on the right. The
  // employment record is the one surface in this set whose subject is a PERSON'S PAPERWORK rather than a
  // person (`users`, a head and shoulders) or the organisation they sit in (`organization`, boxes), and
  // at 20px the silhouette is "wide rectangle with something inside it", which nothing else here is: the
  // two document glyphs in the set are portrait, and the only other landscape outline is a chart on a
  // baseline.
  employmentRecords: (
    <>
      <rect x="2.6" y="5" width="18.8" height="14" rx="2" />
      <circle cx="8.2" cy="11" r="2.1" />
      <path d="M4.9 16.6a3.4 3.4 0 0 1 6.6 0" />
      <path d="M14.2 10h4.4M14.2 13.6h4.4" />
    </>
  ),

  // Org chart — one box above three.
  organization: (
    <>
      <rect x="9" y="3" width="6" height="4.5" rx="1" />
      <rect x="3" y="16.5" width="6" height="4.5" rx="1" />
      <rect x="15" y="16.5" width="6" height="4.5" rx="1" />
      <path d="M12 7.5v4.5M6 16.5V12h12v4.5" />
    </>
  ),

  // An eye — a view scope is how much of the organisation somebody may SEE, and an eye is the one
  // silhouette in this set that is neither a document, a chart, a box nor a person. It sits two rows
  // under `users` (a head and shoulders) and one under `organization` (boxes), and the almond lens
  // survives the 20px rail where another outlined rectangle would not.
  viewScopes: (
    <>
      <path d="M2.8 12s3.4-6 9.2-6 9.2 6 9.2 6-3.4 6-9.2 6-9.2-6-9.2-6z" />
      <circle cx="12" cy="12" r="2.6" />
    </>
  ),

  // Bar chart on a baseline.
  reports: (
    <>
      <path d="M4 20V10M9.3 20V4M14.7 20v-6M20 20v-9" />
      <path d="M2.5 20h19" />
    </>
  ),

  // Document with a tick — a checked record.
  audit: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <path d="M9.5 12.5l1.8 1.8 3.4-3.4" />
    </>
  ),

  // Speech bubble with lines — reported answers.
  leadershipReports: (
    <>
      <path d="M21 15.2a2 2 0 0 1-2 2H8l-4 3.3V5.8a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
      <path d="M8.5 8.5h8M8.5 12h5" />
    </>
  ),

  // Three bars off a left axis — a questionnaire's results, which is a distribution
  // read across rows. Drawn HORIZONTALLY on purpose: `reports` directly above it in
  // the rail is already a vertical bar chart, and two bar charts in one column are
  // one glyph at 20px. Turned on its side the silhouette is unmistakably different —
  // and still a bar chart, unlike leadershipReports' speech bubble two rows down.
  questionnaireResults: (
    <>
      <path d="M4.5 3.5v17" />
      <path d="M7 7h12M7 12h8.5M7 17h5" />
    </>
  ),

  // An almost-closed ring with an arrowhead on its open end — the quarterly CYCLE, which is what
  // muutosjohtaminen is (ADR-0016 §0: the section closes the loop from diagnosis to metrics to decisions
  // and back to actions). Drawn as a bare ring on purpose: `certificateOps` is the set's other circular
  // form and carries a rosette and a ribbon with it, so at 20px that one reads as "seal + circle" and
  // this one as a single open hoop. Nothing else in the set is one stroke that comes back to itself.
  changePrograms: (
    <>
      <path d="M20.2 12a8.2 8.2 0 1 1-2.4-5.8" />
      <path d="M14.4 5.4 17.9 6.3 18.9 2.9" />
    </>
  ),

  // Three bars of different lengths, each starting further right — the muutoskartta's own timeline, which
  // is literally what the screen is: one row per hanke, laid along one shared axis, with a vertical rule
  // at the report's day. The rule is drawn because it is the glyph's only non-horizontal stroke and is
  // what tells it apart from the set's list-ish forms at rail size; without it three bars read as "rows",
  // which is `groups`' job.
  changeMap: (
    <>
      <path d="M4 3.5v17" />
      <path d="M7.5 7h11" />
      <path d="M10 12h8.5" />
      <path d="M7.5 17h6.5" />
    </>
  ),

  // A tick over a calendar's day grid — "my own acts, each with a deadline", which is what MJ-US-E7.2's
  // page is: a deadline-ordered work list whose one write is marking one done. The outline is a date frame
  // rather than `myQuestionnaires`' clipboard (whose clip and option rows are its silhouette) and the tick
  // sits INSIDE it, so at 20px this reads as "calendar + mark" and `audit`'s document tick as "page +
  // mark". Nothing else in the set has the two short head strokes.
  changeActions: (
    <>
      <path d="M4.5 6.5a1 1 0 0 1 1-1h13a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1z" />
      <path d="M4.5 10h15" />
      <path d="M8.5 3.5v3M15.5 3.5v3" />
      <path d="M9 14.8l2.3 2.2 4-4.4" />
    </>
  ),
};

export function NavIcon({ name }: { name: NavIconName }) {
  return (
    <svg
      className="app-nav__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
